/** Processing, Refused - files that were not opened, or that a person set aside (ADR-0212).
 *
 * The reason and the remedy are two elements (`DocumentNote`): what happened, and what to do about
 * it. The remedy is derived on the server from the refusal code, so a better sentence written today
 * applies to a file refused last year, and a code the table does not know says so rather than
 * showing a reason with no way out.
 *
 * A refused file was never kept as evidence; a **set aside** one was read and a person rejected it,
 * with a reason that stays on the document. Both are rows here, with different chips, because
 * "refused" and "rejected" are different acts by different actors.
 */

import { ShieldCheck } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useDocuments } from "../../api/evidence";
import type { DocumentRow } from "../../api/evidence";
import { useDocumentCounts } from "../../api/processing";
import { ReadFailed } from "../../components/Loaded";
import { EmptyState, EMPTY_FILTERS, FilterLine, GridTable, InfoPopover, StatusChip } from "../../components/ui";
import type { FilterDef, FilterState, GridColumn } from "../../components/ui";
import { kindLabel } from "../../lib/documentKinds";
import { DocumentNote } from "../documents/DocumentNote";
import { keepSearch } from "../search";
import { refusalWord } from "../upload/stageChip";
import { DocumentTiles, FileCell, ProcessingFrame } from "./common";
import styles from "./Processing.module.css";

const LIMIT = 200;

export function Refused() {
  const counts = useDocumentCounts();
  const refused = useDocuments("quarantined", { oldestFirst: true, limit: LIMIT });
  const setAside = useDocuments("rejected", { oldestFirst: true, limit: LIMIT });
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

  const reads = [refused, setAside];
  const failure = reads.find((read) => read.error)?.error;
  const pending = reads.some((read) => read.isPending);
  const rows = reads.flatMap((read) => read.data?.rows ?? []).sort((a, b) => a.id.localeCompare(b.id));
  const total = reads.reduce((sum, read) => sum + (read.data?.total ?? 0), 0);

  const codes = [...new Set(rows.flatMap((row) => row.refusal_code ?? []))].sort();
  const reasons: FilterDef = {
    id: "reason",
    label: "Reason",
    mode: "single",
    allLabel: "Any reason",
    options: [
      ...codes.map((code) => ({ value: code, label: refusalWord(code) ?? code })),
      ...(rows.some((row) => row.state === "rejected") ? [{ value: "set_aside", label: "Set aside" }] : []),
    ],
  };
  const wanted = filters.values.reason?.[0];
  const needle = filters.search.trim().toLowerCase();
  const shown = rows.filter(
    (row) =>
      (!wanted || (wanted === "set_aside" ? row.state === "rejected" : row.refusal_code === wanted)) &&
      (!needle || (row.original_filename ?? "").toLowerCase().includes(needle)),
  );

  const nothingYet = !pending && !failure && rows.length === 0;

  const columns: GridColumn<DocumentRow>[] = [
    { id: "file", header: "File", cell: (document) => <FileCell document={document} /> },
    { id: "type", header: "Type", cell: (document) => kindLabel(document.kind) },
    {
      id: "code",
      header: "Reason",
      cell: (document) =>
        document.state === "rejected" ? (
          <StatusChip tone="crit" size="sm">
            Set aside
          </StatusChip>
        ) : (
          <span data-testid="refusal-chip" data-code={document.refusal_code ?? ""}>
            <StatusChip tone="crit" size="sm">
              {refusalWord(document.refusal_code) ?? "Refused"}
            </StatusChip>
          </span>
        ),
    },
    { id: "why", header: "What to do", cell: (document) => <DocumentNote document={document} /> },
    {
      id: "action",
      header: "Action",
      cell: (document) =>
        document.state === "quarantined" ? (
          <Link className={styles.link} to="/upload" search={keepSearch}>
            Upload again
          </Link>
        ) : (
          <span className={styles.none}>–</span>
        ),
    },
  ];

  return (
    <ProcessingFrame
      documentCharts
      tab="Refused"
      nothingYet={nothingYet}
      tiles={
        <DocumentTiles
          counts={counts.data}
          pending={counts.isPending}
          failure={{ error: counts.error, retry: () => void counts.refetch() }}
        />
      }
      filters={
        nothingYet ? undefined : (
          <FilterLine
            filters={[reasons]}
            state={filters}
            onChange={setFilters}
            searchLabel="Search refused files"
            resultCount={pending ? undefined : { shown: shown.length, total: Math.max(total, shown.length), noun: "files" }}
            actions={<InfoPopover infoKey="processing.refused" />}
          />
        )
      }
      table={
        failure ? (
          <ReadFailed
            of="The refused files"
            error={failure}
            onRetry={() => reads.forEach((read) => void read.refetch())}
          />
        ) : (
          <GridTable
            caption="Refused and set aside files"
            testId="refused"
            columns={columns}
            rows={shown}
            rowKey={(document) => document.id}
            rowLabel={(document) => document.original_filename ?? "document"}
            loading={pending}
            empty={
              <EmptyState compact icon={ShieldCheck} title="No file has been refused" testId="refused-empty">
                A file that cannot be read appears here with what to do.
              </EmptyState>
            }
          />
        )
      }
    />
  );
}
